import { Link, type LinkProps } from '@tanstack/react-router'
import { ChevronDown, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import {
  cn,
  FOCUS_RING,
  Select,
  type SelectFooter,
  type SelectOption,
} from '@/components/base'

/**
 * One tab: a pill that fills in once it is the open one, so a strip reads as a
 * set of places at a glance and the baseline under it never moves.
 */
const TAB_CLASS =
  'flex flex-row items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 ' +
  'text-[13px] transition-colors ' +
  FOCUS_RING

const ACTIVE = 'bg-accent-soft font-medium text-primary'

const IDLE =
  'text-muted-foreground hover:bg-line-soft hover:text-foreground disabled:opacity-40'

/**
 * A tab that is one of many records rather than one fixed place: the
 * contract that is open, picked from a list of them. The tab's `label`
 * stands while nothing is picked.
 */
type TabPicker = {
  options: SelectOption[]
  value: string | null
  onChange: (value: string) => void
  searchPlaceholder?: string
  footer?: SelectFooter
}

export type Tab = {
  id: string
  label: string
  icon?: LucideIcon
  disabled?: boolean
  /** A tab that is its own route. Without it the tab switches a view in place. */
  to?: LinkProps['to']
  /** The path segments `to` leaves open, for a tab under a dynamic route. */
  params?: LinkProps['params']
  /** How many rows are behind the tab, where the count helps one choose. */
  count?: number
  /** A state the tab has to carry rather than a size — something waiting
   *  behind it. Set apart from `count` because it asks for attention. */
  badge?: ReactNode
  /** Makes the tab a picker: its press opens the list, its label is what
   *  is picked. */
  picker?: TabPicker
  hidden?: boolean
}

/**
 * The strip of tabs that sits inside a page's header bar.
 *
 * A tab either navigates (`to`) or switches a view in place (`onChange`) — the
 * two are the same control because the reader cannot tell them apart, and
 * making one a link is what gives it a middle-click and an address.
 */
export function Tabs({
  tabs,
  activeTab,
  onChange,
  layout = 'header',
  actions,
  className,
}: {
  tabs: Tab[]
  activeTab?: string
  onChange?: (id: string) => void
  /**
   * What acts on the whole strip rather than on one tab, pinned to its end.
   * Not under `header`: the page's own bar owns that end, and a second set
   * of controls there would sit beside the first.
   */
  actions?: ReactNode
  /**
   * `header` sits in the page's title bar; `row` gets a strip of its own under
   * it, for a second level the page owns; `pane` is the row inside a
   * scrolling detail pane: stuck under the pane's top so it stays reachable
   * while the body scrolls, one row that scrolls sideways, since a second
   * row would push the fields down as the pane narrows.
   */
  layout?: 'header' | 'row' | 'pane'
  className?: string
}) {
  const strip = (
    <div
      className={cn(
        'flex flex-row items-center gap-0.5',
        layout === 'pane' && 'min-w-0 overflow-x-auto [scrollbar-width:thin]',
        className,
      )}
    >
      {tabs
        .filter(tab => !tab.hidden)
        .map(tab => {
          const Icon = tab.icon
          const isActive = tab.id === activeTab
          const skin = cn(TAB_CLASS, isActive ? ACTIVE : IDLE)

          if (tab.picker) {
            const { picker } = tab
            return (
              <Select
                key={tab.id}
                value={picker.value}
                options={picker.options}
                placeholder={tab.label}
                searchPlaceholder={picker.searchPlaceholder}
                footer={picker.footer}
                disabled={tab.disabled}
                onChange={picker.onChange}
                trigger={({ selected, placeholder }) => (
                  <span className={cn(skin, 'max-w-80')}>
                    <span className="truncate">
                      {selected?.label ?? placeholder}
                    </span>
                    <ChevronDown size={13} className="shrink-0" aria-hidden />
                  </span>
                )}
              />
            )
          }

          const body = (
            <>
              {Icon ? <Icon size={14} aria-hidden /> : null}
              {tab.label}
              {tab.count !== undefined ? (
                <span className="text-[10.5px] tabular-nums text-faint">
                  {tab.count}
                </span>
              ) : null}
              {tab.badge}
            </>
          )
          return tab.to ? (
            <Link
              key={tab.id}
              to={tab.to}
              params={tab.params}
              disabled={tab.disabled}
              className={skin}
            >
              {body}
            </Link>
          ) : (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={tab.disabled}
              onClick={() => onChange?.(tab.id)}
              className={skin}
            >
              {body}
            </button>
          )
        })}
    </div>
  )

  if (layout === 'header') return strip
  return (
    <div
      className={cn(
        'flex shrink-0 flex-row items-center gap-3 border-b py-1.5',
        layout === 'row'
          ? 'border-line-soft px-4'
          : 'sticky top-0 z-30 border-border bg-card px-3',
      )}
    >
      {strip}
      {actions ? (
        <div className="ml-auto flex shrink-0 flex-row items-center gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  )
}
